var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import Overflow from 'rc-overflow';
import warning from 'rc-util/lib/warning';
import OutlinedDown from 'acud-icon/lib/icons/OutlinedDown';
import OutlinedUp from 'acud-icon/lib/icons/OutlinedUp';
import OutlinedRight from 'acud-icon/lib/icons/OutlinedRight';
import SubMenuList from './SubMenuList';
import { parseChildren, parseChildrenWithIcon } from '../utils/nodeUtil';
import MenuContextProvider, { MenuContext } from '../context/MenuContext';
import MenuContextW from '../../MenuContext';
import useMemoCallback from '../hooks/useMemoCallback';
import PopupTrigger from './PopupTrigger';
import Icon from '../Icon';
import useActive from '../hooks/useActive';
import { warnItemProp } from '../utils/warnUtil';
import useDirectionStyle from '../hooks/useDirectionStyle';
import InlineSubMenuList from './InlineSubMenuList';
import { PathTrackerContext, PathUserContext, useFullPath, useMeasure } from '../context/PathContext';
import { useMenuId } from '../context/IdContext';
const InternalSubMenu = (props) => {
    const { style, className, title, preTitle, eventKey, warnKey, sub, disabled, internalPopupClose, children, 
    // Icons
    itemIcon, expandIcon, 
    // Popup
    popupClassName, popupOffset, popupPlacement, inlineCollapsed, 
    // Events
    onClick, onMouseEnter, onMouseLeave, onTitleClick, onTitleMouseEnter, onTitleMouseLeave } = props, restProps = __rest(props, ["style", "className", "title", "preTitle", "eventKey", "warnKey", "sub", "disabled", "internalPopupClose", "children", "itemIcon", "expandIcon", "popupClassName", "popupOffset", "popupPlacement", "inlineCollapsed", "onClick", "onMouseEnter", "onMouseLeave", "onTitleClick", "onTitleMouseEnter", "onTitleMouseLeave"]);
    const domDataId = useMenuId(eventKey);
    const { prefixCls, mode, openKeys, 
    // Disabled
    disabled: contextDisabled, overflowDisabled, 
    // ActiveKey
    activeKey, 
    // SelectKey
    selectedKeys, inlineCollapsed: inlineCollapsedC, 
    // Icon
    itemIcon: contextItemIcon, expandIcon: contextExpandIcon, 
    // Events
    onItemClick, onOpenChange, onActive } = React.useContext(MenuContext);
    const menuContextWValue = React.useContext(MenuContextW);
    const { notFirstSub, menuMode } = menuContextWValue;
    let firstSubTitle = null;
    if (menuMode === 'inline' && inlineCollapsedC && !notFirstSub) {
        firstSubTitle = <div className={`${prefixCls}-pop-sub-title`}>{preTitle}</div>;
    }
    const { isSubPathKey } = React.useContext(PathUserContext);
    const connectedPath = useFullPath();
    const subMenuPrefixCls = `${prefixCls}-submenu`;
    const mergedDisabled = contextDisabled || disabled;
    const elementRef = React.useRef();
    const popupRef = React.useRef();
    // ================================ Warn ================================
    if (process.env.NODE_ENV !== 'production' && warnKey) {
        warning(false, 'SubMenu should not leave undefined `key`.');
    }
    // ================================ Icon ================================
    const mergedItemIcon = itemIcon || contextItemIcon;
    const mergedExpandIcon = expandIcon || contextExpandIcon;
    // ================================ Open ================================
    const originOpen = openKeys.includes(eventKey);
    const open = !overflowDisabled && originOpen;
    // =============================== Select ===============================
    const childrenSelected = isSubPathKey(selectedKeys, eventKey);
    // =============================== Active ===============================
    const _a = useActive(eventKey, mergedDisabled, onTitleMouseEnter, onTitleMouseLeave), { active } = _a, activeProps = __rest(_a, ["active"]);
    // Fallback of active check to avoid hover on menu title or disabled item
    const [childrenActive, setChildrenActive] = React.useState(false);
    const triggerChildrenActive = (newActive) => {
        if (!mergedDisabled) {
            setChildrenActive(newActive);
        }
    };
    const onInternalMouseEnter = domEvent => {
        triggerChildrenActive(true);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter({
            key: eventKey,
            domEvent
        });
    };
    const onInternalMouseLeave = domEvent => {
        triggerChildrenActive(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave({
            key: eventKey,
            domEvent
        });
    };
    const mergedActive = React.useMemo(() => {
        if (active) {
            return active;
        }
        if (mode !== 'inline') {
            return childrenActive || isSubPathKey([activeKey], eventKey);
        }
        return false;
    }, [mode, active, activeKey, childrenActive, eventKey, isSubPathKey]);
    // ========================== DirectionStyle ==========================
    const directionStyle = useDirectionStyle(className, sub);
    // =============================== Events ===============================
    // >>>> Title click
    const onInternalTitleClick = e => {
        // Skip if disabled
        if (mergedDisabled) {
            return;
        }
        onTitleClick === null || onTitleClick === void 0 ? void 0 : onTitleClick({
            key: eventKey,
            domEvent: e
        });
        // Trigger open by click when mode is `inline`
        if (mode === 'inline') {
            onOpenChange(eventKey, !originOpen);
        }
    };
    // >>>> Context for children click
    const onMergedItemClick = useMemoCallback((info) => {
        onClick === null || onClick === void 0 ? void 0 : onClick(warnItemProp(info));
        onItemClick(info);
    });
    // >>>>> Visible change
    const onPopupVisibleChange = (newVisible) => {
        if (mode !== 'inline') {
            onOpenChange(eventKey, newVisible);
        }
    };
    /**
     * Used for accessibility. Helper will focus element without key board.
     * We should manually trigger an active
     */
    const onInternalFocus = () => {
        onActive(eventKey);
    };
    // =============================== Render ===============================
    const popupId = domDataId && `${domDataId}-popup`;
    // >>>>> Title
    let titleNode = (<div role="menuitem" style={directionStyle} className={`${subMenuPrefixCls}-title`} tabIndex={mergedDisabled ? null : -1} ref={elementRef} title={typeof title === 'string' ? title : null} data-menu-id={overflowDisabled && domDataId ? null : domDataId} aria-expanded={open} aria-haspopup aria-controls={popupId} aria-disabled={mergedDisabled} onClick={onInternalTitleClick} onFocus={onInternalFocus} {...activeProps}>
            {title}

            {/* Only non-horizontal mode shows the icon */}
            <Icon icon={mode !== 'horizontal' ? mergedExpandIcon : null} props={Object.assign(Object.assign({}, props), { isOpen: open, 
            // [Legacy] Not sure why need this mark
            isSubMenu: true })}/>
            {mode === 'vertical'
            || mode === 'inline' && inlineCollapsed
            ? <OutlinedRight className={`${subMenuPrefixCls}-expand-icon`}/>
            : (open
                ? <OutlinedUp className={`${subMenuPrefixCls}-expand-icon`}/>
                : <OutlinedDown className={`${subMenuPrefixCls}-expand-icon`}/>)}
        </div>);
    // Cache mode if it change to `inline` which do not have popup motion
    const triggerModeRef = React.useRef(mode);
    if (mode !== 'inline' && connectedPath.length > 1) {
        triggerModeRef.current = 'vertical';
    }
    else {
        triggerModeRef.current = mode;
    }
    if (!overflowDisabled) {
        const triggerMode = triggerModeRef.current;
        // Still wrap with Trigger here since we need avoid react re-mount dom node
        // Which makes motion failed
        titleNode = (<PopupTrigger mode={triggerMode} prefixCls={subMenuPrefixCls} visible={!internalPopupClose && open && mode !== 'inline'} popupClassName={classNames(popupClassName, {
                [`${prefixCls}-inline-title-pop`]: !!firstSubTitle
            })} popupOffset={popupOffset} popupPlacement={popupPlacement} popup={<MenuContextW.Provider value={Object.assign(Object.assign({}, menuContextWValue), { notFirstSub: true })}>
                        <MenuContextProvider 
            // Special handle of horizontal mode
            mode={triggerMode === 'horizontal' ? 'vertical' : triggerMode}>
                            {firstSubTitle}
                            <SubMenuList id={popupId} ref={popupRef}>
                                {children}
                            </SubMenuList>
                        </MenuContextProvider>
                    </MenuContextW.Provider>} disabled={mergedDisabled} onVisibleChange={onPopupVisibleChange}>
                {titleNode}
            </PopupTrigger>);
    }
    // >>>>> Render
    return (<MenuContextProvider onItemClick={onMergedItemClick} mode={mode === 'horizontal' ? 'vertical' : mode} itemIcon={mergedItemIcon} expandIcon={mergedExpandIcon}>
            <Overflow.Item role="none" {...restProps} component="li" style={style} className={classNames(subMenuPrefixCls, `${subMenuPrefixCls}-${mode}`, className, {
            [`${subMenuPrefixCls}-open`]: open,
            [`${subMenuPrefixCls}-active`]: mergedActive,
            [`${subMenuPrefixCls}-selected`]: childrenSelected,
            [`${subMenuPrefixCls}-disabled`]: mergedDisabled
        })} onMouseEnter={onInternalMouseEnter} onMouseLeave={onInternalMouseLeave}>
                {titleNode}

                {/* Inline mode */}
                {!overflowDisabled && (<InlineSubMenuList id={popupId} open={open} keyPath={connectedPath}>
                        {parseChildrenWithIcon(children, className, directionStyle)}
                    </InlineSubMenuList>)}
            </Overflow.Item>
        </MenuContextProvider>);
};
export default function SubMenu(props) {
    const { eventKey, children } = props;
    const connectedKeyPath = useFullPath(eventKey);
    const childList = parseChildren(children, connectedKeyPath);
    // ==================== Record KeyPath ====================
    const measure = useMeasure();
    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
        if (measure) {
            measure.registerPath(eventKey, connectedKeyPath);
            return () => {
                measure.unregisterPath(eventKey, connectedKeyPath);
            };
        }
    }, [connectedKeyPath]);
    let renderNode;
    // ======================== Render ========================
    if (measure) {
        renderNode = childList;
    }
    else {
        renderNode = <InternalSubMenu {...props}>{childList}</InternalSubMenu>;
    }
    return (<PathTrackerContext.Provider value={connectedKeyPath}>
            {renderNode}
        </PathTrackerContext.Provider>);
}
